import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ShieldAlert, Trash2 } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { cn } from "@/lib/cn";
import type { CampaignActivationContact, CampaignActivationPreview } from "@/types/campaign";

interface Props {
  open: boolean;
  preview: CampaignActivationPreview | null;
  submitting?: boolean;
  deleting?: boolean;
  onCancel: () => void;
  onDelete: (contactIds: string[]) => void;
  onActivate: () => void;
}

/**
 * Activation dry-run for contacts that still fail validation. Ready contacts
 * are counted in the header but not listed.
 */
export function CampaignActivationPreviewModal({
  open,
  preview,
  submitting = false,
  deleting = false,
  onCancel,
  onDelete,
  onActivate,
}: Props) {
  const contacts = preview?.contacts ?? [];
  const [selected, setSelected] = useState<string[]>([]);
  const blocked = contacts.filter((c) => c.issues.length > 0);

  useEffect(() => {
    if (open) setSelected([]);
  }, [open, preview]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const deletableIds = blocked.filter((c) => !c.linked_to_crm).map((c) => c.contact_id);
  const allDeletableSelected =
    deletableIds.length > 0 && deletableIds.every((id) => selectedSet.has(id));
  const busy = submitting || deleting;
  const canActivate =
    Boolean(preview) &&
    (preview?.blocked ?? 0) === 0 &&
    (preview?.total ?? 0) > 0 &&
    !preview?.schedule_in_past;

  if (!open || !preview) return null;

  const toggleAll = () => {
    setSelected(allDeletableSelected ? [] : deletableIds);
  };

  const toggleRow = (contact: CampaignActivationContact) => {
    if (contact.linked_to_crm) return;
    setSelected((current) =>
      current.includes(contact.contact_id)
        ? current.filter((id) => id !== contact.contact_id)
        : [...current, contact.contact_id],
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-900/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-activation-preview-title"
        className="bg-white rounded-2xl shadow-2xl w-[calc(100vw-2rem)] max-w-4xl overflow-hidden ring-1 ring-slate-500/10"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-bridge-border/60 bg-slate-50">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-amber-100 text-amber-800">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 id="campaign-activation-preview-title" className="text-base font-bold text-slate-900">
              {preview.blocked} contact{preview.blocked === 1 ? "" : "s"} need attention before activating
            </h2>
            <p className="text-xs text-slate-600 mt-0.5">
              Tick contacts to delete from Atlas, then activate when nobody still has an error.
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                {preview.ready} ready
              </span>
              <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-800">
                {preview.blocked} need attention
              </span>
            </div>
          </div>
        </div>

        {preview.schedule_in_past && (
          <div className="px-5 py-3 border-b border-amber-200 bg-amber-50 text-sm text-amber-900">
            The scheduled send time is in the past, so this campaign would never run. Edit the
            campaign and pick a new time, then try Activate again.
          </div>
        )}

        {preview.total === 0 && (
          <div className="px-5 py-3 border-b border-amber-200 bg-amber-50 text-sm text-amber-900">
            There are no contacts on this campaign&apos;s labels yet.
          </div>
        )}

        <div className="px-5 py-3 border-b border-bridge-border/60 bg-white">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-800 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-bridge-border accent-bridge-accent"
              checked={allDeletableSelected}
              disabled={deletableIds.length === 0 || busy}
              ref={(el) => {
                if (el) el.indeterminate = selected.length > 0 && !allDeletableSelected;
              }}
              onChange={toggleAll}
            />
            Select all that can be deleted
            <span className="font-normal text-bridge-muted">
              ({selected.length} of {deletableIds.length} selected)
            </span>
          </label>
          <p className="mt-2 text-xs text-slate-600 leading-relaxed">
            Contacts synced with Alto cannot be deleted here. Fix the issues first, or remove
            those contacts from the selected label.
          </p>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4 space-y-4">
          {blocked.length > 0 && (
            <ContactTable
              title={`${blocked.length} need attention`}
              tone="blocked"
              contacts={blocked}
              selectedSet={selectedSet}
              busy={busy}
              onToggle={toggleRow}
            />
          )}
        </div>

        <div className="px-5 py-4 border-t border-bridge-border/60 bg-slate-50 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={deleting}
            disabled={selected.length === 0 || busy}
            onClick={() => onDelete(selected)}
          >
            <Trash2 size={14} />
            Delete
          </Button>
          <Button variant="primary" loading={submitting} disabled={!canActivate || busy} onClick={onActivate}>
            Activate campaign
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ContactTable({
  title,
  tone,
  contacts,
  selectedSet,
  busy,
  onToggle,
}: {
  title: string;
  tone: "ready" | "blocked";
  contacts: CampaignActivationContact[];
  selectedSet: Set<string>;
  busy: boolean;
  onToggle: (contact: CampaignActivationContact) => void;
}) {
  const styles =
    tone === "ready"
      ? {
          border: "border-emerald-200",
          header: "bg-emerald-50",
          badge: "bg-emerald-100 text-emerald-800",
        }
      : {
          border: "border-amber-200",
          header: "bg-amber-50",
          badge: "bg-amber-100 text-amber-800",
        };

  return (
    <div className={cn("rounded-xl border overflow-hidden", styles.border)}>
      <div className={cn("flex items-center gap-2.5 px-3 py-2 border-b", styles.header, styles.border)}>
        <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide", styles.badge)}>
          {tone === "ready" ? "Ready" : "Needs attention"}
        </span>
        <span className="text-sm font-medium text-slate-800">{title}</span>
      </div>
      <ul className="divide-y divide-bridge-border/50">
        {contacts.map((contact) => (
          <li key={contact.contact_id} className="px-3 py-2.5">
            <label
              className={cn(
                "flex items-start gap-2.5",
                contact.linked_to_crm ? "cursor-not-allowed opacity-80" : "cursor-pointer",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-bridge-border accent-bridge-accent"
                checked={selectedSet.has(contact.contact_id)}
                disabled={contact.linked_to_crm || busy}
                onChange={() => onToggle(contact)}
                aria-label={`Select ${contact.name ?? contact.contact_id}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-800">
                    {contact.name?.trim() || contact.contact_id}
                  </span>
                  {contact.crm_sync_status && (
                    <span className="text-[11px] font-medium text-bridge-muted">
                      Alto: {contact.crm_sync_status}
                    </span>
                  )}
                  {contact.linked_to_crm && (
                    <span className="text-[11px] font-medium text-slate-500">
                      Linked to Alto — cannot delete
                    </span>
                  )}
                </div>
                {contact.issues.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {contact.issues.map((issue) => (
                      <li key={`${contact.contact_id}-${issue.code}`} className="text-xs text-amber-900">
                        {issue.message || issue.code}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
